import {
  AfterContentInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { throttle } from 'lodash';
import { fromEvent, merge, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { WindowRefService } from 'i2g-ng-utils';

@Directive({
  selector: '[i2gDynamicResize]',
})
export class DynamicResizeDirective
  implements OnInit, OnDestroy, AfterContentInit {
  @Input() formControl: AbstractControl;
  /**
   * if set to true, allow the component to resize
   */
  @Input() i2gDynamicResize = true;

  private _borderVertical: number;
  private _borderHorizontal: number;
  private _paddingRight: number;
  private _dimensionsInitialized: boolean;
  private _el: any;
  private _ghost: any;
  private _window: any;
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _resizeThrottled = throttle(this.resize, 50);

  constructor(
    private _windowRefService: WindowRefService,
    private _elementRef: ElementRef,
    private _renderer: Renderer2
  ) {}

  ngOnInit() {
    this._el = this._elementRef.nativeElement;
    this._ghost = this._el.cloneNode();
    this._window = this._windowRefService.nativeWindow;

    this._renderer.setStyle(this._ghost, 'position', 'absolute');
    this._renderer.setStyle(this._ghost, 'left', '-9999px');
    this._renderer.setAttribute(this._ghost, 'tabindex', '-1');

    this._el.parentElement.appendChild(this._ghost);

    merge(
      fromEvent(this._el, 'input').pipe(
        map((e: KeyboardEvent) => (e.target as HTMLInputElement).value)
      ),
      this.formControl.valueChanges
    )
      .pipe(takeUntil(this._destroyed))
      .subscribe((value) => this.handleValueChange(value));
  }

  ngAfterContentInit() {
    setTimeout(() => {
      this._renderer.setAttribute(
        this._ghost,
        'placeholder',
        this._el.getAttribute('placeholder')
      );

      const elOffsetWidth = this._el.offsetWidth;

      this._renderer.setStyle(this._ghost, 'width', `${elOffsetWidth}px`);

      if (this._ghost && this._el) {
        this._ghost.value = this._el.value;
      }

      const scrollWidth =
        this._ghost.offsetWidth -
        this._borderHorizontal -
        this._ghost.clientWidth;

      if (scrollWidth > this._paddingRight) {
        this._renderer.setStyle(
          this._ghost,
          'width',
          `${elOffsetWidth + (scrollWidth - this._paddingRight)}px`
        );
      }

      // set timeout so ghost.scrollHeight has enough time to be set
      setTimeout(() => this._resizeThrottled(), 500);
    });
  }

  handleValueChange(value: string) {
    if (this._ghost) {
      this._ghost.value = value;
    }

    this._resizeThrottled();
  }

  resize() {
    if (!this.i2gDynamicResize) {
      return;
    }

    this.initValues();

    if (this._ghost.scrollHeight > 0) {
      this._renderer.setStyle(
        this._el,
        'height',
        `${this._ghost.scrollHeight + this._borderVertical}px`
      );
    }
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private initValues() {
    if (!this._dimensionsInitialized) {
      const computedStyles = this._window.getComputedStyle(this._el);
      const borderTop = computedStyles.getPropertyValue('border-top-width');
      const borderLeft = computedStyles.getPropertyValue('border-left-width');
      const borderRight = computedStyles.getPropertyValue('border-right-width');
      const borderBottom = computedStyles.getPropertyValue(
        'border-bottom-width'
      );
      const paddingRight = computedStyles.getPropertyValue('padding-right');

      this._borderHorizontal =
        parseInt(borderLeft, 10) + parseInt(borderRight, 10);
      this._paddingRight = parseInt(paddingRight, 10);
      this._borderVertical =
        parseInt(borderTop, 10) + parseInt(borderBottom, 10);

      this._dimensionsInitialized = true;
    }
  }
}
